import { MAX_PRIORITY } from '@/constants'

export type ComparablePriorityState = {
  isMain: boolean
  priority?: number
}

export const getEffectivePriority = (item: { priority?: number }) =>
  item.priority ?? MAX_PRIORITY

export const toPayloadPriority = (item: { priority?: number }) => {
  const priority = getEffectivePriority(item)
  return priority < MAX_PRIORITY ? priority : undefined
}

export const getComparableState = (item: {
  isMain?: boolean
  priority?: number
}): ComparablePriorityState => ({
  isMain: item?.isMain === true,
  priority: toPayloadPriority(item),
})

export const hasComparableStateChanged = (
  currentState: ComparablePriorityState,
  originalState: ComparablePriorityState
) =>
  currentState.isMain !== originalState.isMain ||
  currentState.priority !== originalState.priority

export const expandChangedWithUnprioritizedRange = (
  currentIds: string[],
  originalIds: string[],
  changedIds: Set<string>,
  originalRawPriorityById: Record<string, number | undefined>
) => {
  const hasUnprioritizedChanged = [...changedIds].some(
    (id) => originalRawPriorityById[id] === MAX_PRIORITY
  )

  if (!hasUnprioritizedChanged) return changedIds

  const maxLen = Math.max(currentIds.length, originalIds.length)
  let firstChangedIdx = -1
  let lastChangedIdx = -1
  for (let idx = 0; idx < maxLen; idx += 1) {
    if (currentIds[idx] !== originalIds[idx]) {
      if (firstChangedIdx === -1) firstChangedIdx = idx
      lastChangedIdx = idx
    }
  }

  if (firstChangedIdx === -1 || lastChangedIdx === -1) return changedIds

  const expanded = new Set(changedIds)
  currentIds
    .slice(firstChangedIdx, lastChangedIdx + 1)
    .forEach((id) => expanded.add(id))
  return expanded
}

export const getPrioritizedCountAfterReorder = (
  currentIds: string[],
  originalIds: string[],
  originalRawPriorityById: Record<string, number | undefined>
) => {
  const initialPrioritizedCount = originalIds.filter(
    (id) => (originalRawPriorityById[id] ?? MAX_PRIORITY) < MAX_PRIORITY
  ).length

  let lastChangedIdx = -1
  const maxLen = Math.max(currentIds.length, originalIds.length)
  for (let idx = 0; idx < maxLen; idx += 1) {
    if (currentIds[idx] !== originalIds[idx]) {
      lastChangedIdx = idx
    }
  }

  if (lastChangedIdx === -1) return initialPrioritizedCount
  if (lastChangedIdx < initialPrioritizedCount) return initialPrioritizedCount

  return Math.max(initialPrioritizedCount, lastChangedIdx + 1)
}

export const normalizePriorityOrder = <
  T extends { isMain?: boolean; priority?: number },
>(
  items: T[]
): T[] => {
  const safeItems = Array.isArray(items) ? items : []
  const byPriority = (a: T, b: T) =>
    getEffectivePriority(a) - getEffectivePriority(b)
  const main = safeItems.filter((i) => i.isMain === true).sort(byPriority)
  const others = safeItems.filter((i) => i.isMain !== true)
  const prioritizedOthers = others
    .filter((i) => getEffectivePriority(i) < MAX_PRIORITY)
    .sort(byPriority)

  const normalized = new Map<T, T>()
  main.forEach((item, idx) =>
    normalized.set(item, { ...item, priority: idx + 1 })
  )
  prioritizedOthers.forEach((item, idx) =>
    normalized.set(item, { ...item, priority: idx + 1 })
  )
  return safeItems.map((item) => normalized.get(item) ?? item)
}
